// supportGuide — what the support agent may point at on screen during an in-app
// support call ("Talk to support", the ⌘K palette), as pure functions.
//
// The agent never names a URL or a CSS selector. It names a TARGET id from the
// catalogue below, and the catalogue is built from the pages THIS person can
// reach (navModulesFor), so the agent can only open a page the caller could open
// themselves, and only light up a button that was deliberately tagged with
// data-guide="<id>". It points; it never clicks.
//
// Two kinds of target:
//   page:<module id>  open that page (every module the person can reach);
//   <tagged id>       open the page it lives on if needed, then highlight the
//                     element carrying data-guide="<id>".
// To add one: put data-guide="<id>" on the element and a row in TAGGED.

const GUIDE_ATTR = 'data-guide'

export interface GuideTarget {
  id:    string
  label: string
  /** The route to be on; null = wherever the caller is (an element on every page). */
  path:  string | null
  /** False for a page target: open it, nothing to highlight. */
  highlight: boolean
}

interface TaggedTarget {
  id:    string
  label: string
  /** Module keys whose page carries this element; null = every page. */
  modules: string[] | null
}

/** Hand-tagged elements. Keep the ids stable: the agent's prompt names them. */
const TAGGED: TaggedTarget[] = [
  { id: 'help.goto',              label: 'Search / Go to button (opens the menu with Report a problem and Talk to support)', modules: null },
  { id: 'organogram.add_person',  label: 'Organogram: Add person', modules: ['organogram', 'farm_organogram', 'exporter_organogram'] },
  { id: 'deliveries.new',         label: 'Deliveries: New delivery', modules: ['deliveries'] },
]

export interface NavItem { id: string; key: string; label: string; path: string }

/** Every target this person may be shown, from the pages they can reach. */
export function guideCatalogue(nav: NavItem[]): GuideTarget[] {
  const pages: GuideTarget[] = nav.map(m => ({ id: `page:${m.id}`, label: m.label, path: m.path, highlight: false }))
  const tagged: GuideTarget[] = []
  for (const t of TAGGED) {
    if (t.modules === null) { tagged.push({ id: t.id, label: t.label, path: null, highlight: true }); continue }
    const home = nav.find(m => t.modules!.includes(m.key))
    if (home) tagged.push({ id: t.id, label: t.label, path: home.path, highlight: true })
  }
  return [...tagged, ...pages]
}

export type GuideResolution =
  | { ok: true; target: GuideTarget }
  | { ok: false; say: string }

/** The catalogue row for what the agent asked for, or the sentence to hand back. */
export function resolveGuide(raw: unknown, catalogue: GuideTarget[]): GuideResolution {
  const id = typeof raw === 'string' ? raw.trim() : ''
  if (!id) return { ok: false, say: 'No target given. Call screen_where_am_i for the list of targets.' }
  const target = catalogue.find(t => t.id === id)
  if (target) return { ok: true, target }
  return { ok: false, say: `"${id.slice(0, 60)}" is not something I can show this person. Call screen_where_am_i for the list, or describe the steps in words.` }
}

/** Do we have to change page first? A page target or a tagged one off this page. */
export function needsNavigation(target: GuideTarget, pathname: string): boolean {
  if (target.path === null) return false
  return pathname !== target.path
}

/** The selector for a tagged id. Ids are from TAGGED, so no escaping is needed;
 *  anything else is refused before it gets here. */
export const guideSelector = (id: string): string => `[${GUIDE_ATTR}="${id}"]`

/**
 * The tool result for screen_where_am_i: where the caller is, and what can be
 * shown. Plain text, short, because the model reads it every time.
 */
export function screenSummary(input: {
  pathname: string; catalogue: GuideTarget[]; controls?: string[]; errors?: string[]
}): string {
  // The page's NAME only, never the path: a path can carry a record id, and the
  // vendor keeps tool results for 30 days.
  const here = input.catalogue.find(t => !t.highlight && t.path === input.pathname)
  const tagged = input.catalogue.filter(t => t.highlight)
  const pages = input.catalogue.filter(t => !t.highlight)
  const controls = input.controls ?? []
  return [
    `The caller is on: ${here?.label ?? 'a page that is not in the menu'}.`,
    `Buttons and fields on this screen you can ring (use button:<label>): ${controls.map(l => `button:${l}`).join('; ') || 'none found'}.`,
    `Tagged buttons (they open their page first if needed): ${tagged.map(t => `${t.id} = ${t.label}`).join('; ') || 'none'}.`,
    `Pages you can open: ${pages.map(t => `${t.id} = ${t.label}`).join('; ') || 'none'}.`,
    ...(input.errors?.length ? [`Errors showing on this screen (# is a hidden number): ${input.errors.map(e => `"${e}"`).join('; ')}.`] : []),
  ].join('\n')
}

/**
 * screen_show_me's answer when it OPENED a page: the new page's screen in the
 * same result, so she can ring the first step's button in the same turn. A
 * "Screen now:" sent after a page change never starts a reply by itself, so
 * without this she opened the page and then waited for the caller to speak.
 */
export function openedPage(label: string, screenNow: string | null): string {
  return screenNow
    ? `Opened ${label} on their screen. Nothing is rung yet: call screen_show_me now with the button for the next step from this list, then tell them to tap it. ${screenNow}`
    : `Opened ${label} on their screen. Its buttons arrive in the next "Screen now:".`
}
